<template>
    <div class="block-editor whatsapp-business-message">
        <a-spin class="spinner" :spinning="loading">
            <div class="editor-row">
                <div class="editor-row-title">Channel</div>
                <a-select class="classic-new-select" placeholder="Whatsapp Business Channel" v-model="clone.channelId">
                    <a-select-option v-for="channel in businessChannels" :key="channel._id" :value="channel._id">
                        {{ channel.name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="editor-row" v-if="clone.channelId">
                <div class="editor-row-title">
                    Template
                    <div class="material-icons" @click="previewMode = true">visibility</div>
                </div>
                <a-select
                    class="classic-new-select"
                    placeholder="Whatsapp Business Channel"
                    @change="selectTemplate"
                    :value="selectedTemplateId"
                >
                    <a-select-option v-for="template of templates" :key="template.id" :value="template.id">
                        {{ template.elementName || template.name }}
                    </a-select-option>
                </a-select>
            </div>
            <div class="editor-row" v-if="selectedTemplateId">
                <div class="editor-row-title">Phone Number</div>
                <TiptapEditor
                    placeholder="Phone Number"
                    :show-attributes="true"
                    :max-character-count="500"
                    v-model="clone.phoneNumber"
                />
                <div class="phone-number-hint">
                    Sabit değer girecekseniz ülke kodunu da girmeniz gerekmektedir (örn: 905551234567).
                </div>
            </div>
            <div class="wpa-variables" v-if="selectedTemplateId && variables.length > 0">
                <div class="editor-row" v-for="(variable, index) of variables" :key="`variable-${index}`">
                    <div class="editor-row-title">Variable (#{{ index + 1 }})</div>
                    <TiptapEditor
                        :placeholder="`Variable (#${index + 1})`"
                        :show-attributes="true"
                        :max-character-count="500"
                        :value="getVariableValue(index)"
                        @change="setVariableValue(index, $event)"
                    />
                </div>
            </div>
            <div
                class="image-picker"
                v-if="hasMediaHeader"
            >
                <div class="editor-row select-media-type">
                    <div class="editor-row-title">Media Type</div>
                    <a-select v-model="mediaType" placeholder="Select Media Type" style="width: 100%">
                        <a-select-option value="static">Static</a-select-option>
                        <a-select-option value="dynamic">Dynamic</a-select-option>
                    </a-select>
                </div>
                <div v-if="mediaType === 'dynamic'" class="editor-row">
                    <div class="editor-row-title">Media URL</div>
                    <TiptapEditor
                        placeholder="Dynamic Media URL"
                        :show-attributes="true"
                        :max-character-count="500"
                        automation-mode
                        v-model="externalUrl"
                    />
                </div>
                <div v-else-if="mediaType === 'static'" class="editor-row">
                    <div
                        class="editor-row-title"
                        v-tippy="{
                            followCursor: true,
                            placement: 'bottom-end',
                        }"
                        :content="'Görseli Özelleştirmek İstiyorsanız.'"
                    >
                        Media
                        <span class="optional">{{ $t("whatsapp_business_api.optional") }}</span>
                        <a-icon type="question-circle" />
                    </div>
                    <div class="media-preview" v-if="media">
                        <div class="media-preview-content">
                            <div class="media-image">
                                <img :src="media.data" alt="" />
                            </div>
                            <div class="media-preview-name">
                                {{ media.name }}
                            </div>
                        </div>
                        <div class="media-preview-delete" @click="deleteMedia">
                            <div class="material-icons">delete</div>
                        </div>
                    </div>
                    <div class="media-drag" v-else>
                        <div class="input">
                            <input type="file" id="media-source" :accept="getAcceptType()" @change="handleFileUpload" />
                        </div>
                        <div class="material-icons">image</div>
                        <div class="content">Click here to upload a picture.</div>
                    </div>
                    <span class="description">
                        <div class="input-warn">
                            <div>
                                <b>Note:</b>Following are the file formats and their respective sizes which are accepted
                                for upload
                            </div>
                            <ul class="list-item">
                                <li>Image: JPEG and PNG (Max size 2 MB)</li>
                                <li>Video: MP4 (Max size 20 MB)</li>
                                <li>Document: (Max size 2 MB)</li>
                            </ul>
                        </div>
                    </span>
                </div>
            </div>
        </a-spin>
        <WhatsappMessagePreview
            :body="getTemplateParameters(clone.template).body"
            :footer="getTemplateParameters(clone.template).footer"
            :header-media-type="getTemplateParameters(clone.template).headerType"
            :header-text="getTemplateParameters(clone.template).headerText"
            :buttons="getTemplateParameters(clone.template).buttons"
            :headerMediaURL="getTemplateParameters(clone.template).headerMediaURL"
            v-if="clone.template"
        />
    </div>
</template>

<script>
import log2 from "../../../../../../utils/log2";
import _ from "lodash";
import TiptapEditor from "../common/TiptapEditor";
import { mapActions, mapState } from "vuex";
import TemplateMessageSelector from "../../../../Whatsapp/pages/broadcast/create/TemplateMessageSelector.vue";
import AttributePicker from "../pickers/AttributePicker.vue";
import { uploadAsset } from "../../../../../../api/assetUpload";
import axios from "axios";
import Modal2 from "../../../../../../components/common/Modal2.vue";
import WhatsappMessagePreview from "../../../.././WhatsappBusinessGupshup/WhatsappMessagePreview.vue";

const log = log2("WhatsappBusinessMessage");
export default {
    name: "WhatsappBusinessMessage",
    components: { WhatsappMessagePreview, Modal2, AttributePicker, TemplateMessageSelector, TiptapEditor },
    props: ["value"],
    data() {
        return {
            loading: false,
            clone: _.cloneDeep(this.value),
            templates: [],
            selectedTemplateId: null,
            variables: [],
            media: null,
            useDefault: false,
            previewMode: false,
            externalUrl: null,
            mediaType: "static",
        };
    },
    computed: {
        ...mapState({
            siteId: (state) => state.site.site._id,
            businessChannels: (state) =>
                state.channel.channels.filter((channel) => channel.type === "whatsapp-business-api" || channel.type === "whatsapp-business-api-supsis")
        }),
        hasMediaHeader() {
            const template = this.clone.template;
            if (!template) return false;

            // Meta format için
            if (template.format === "meta" && template.components) {
                const headerComponent = template.components.find((c) => c.type === "HEADER");
                return headerComponent && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerComponent.format);
            }

            // Gupshup format için
            return ["IMAGE", "VIDEO", "DOCUMENT"].includes(template.templateType);
        },
    },
    methods: {
        ...mapActions({
            getTemplates: "whatsapp/getTemplates",
        }),
        getTemplateParameters(template) {
            if (!template) {
                return {
                    headerType: "TEXT",
                    headerMediaType: "TEXT",
                    headerMediaURL: null,
                    headerText: null,
                    footer: null,
                    body: "",
                    buttons: [],
                };
            }

            // Meta format için (whatsapp-business-api-supsis)
            if (template.format === "meta" && template.components) {
                const components = template.components || [];
                const headerComponent = components.find((c) => c.type === "HEADER");
                const bodyComponent = components.find((c) => c.type === "BODY");
                const footerComponent = components.find((c) => c.type === "FOOTER");
                const buttonsComponent = components.find((c) => c.type === "BUTTONS");

                let headerType = "TEXT";
                let headerMediaURL = null;
                let headerText = null;

                if (headerComponent) {
                    if (headerComponent.format === "IMAGE") {
                        headerType = "IMAGE";
                        headerMediaURL = this.mediaType === "dynamic"
                            ? ""
                            : this.media?.asset?.url || null;
                    } else if (headerComponent.format === "VIDEO") {
                        headerType = "VIDEO";
                        headerMediaURL = this.mediaType === "dynamic"
                            ? ""
                            : this.media?.asset?.url || null;
                    } else if (headerComponent.format === "DOCUMENT") {
                        headerType = "DOCUMENT";
                        headerMediaURL = this.mediaType === "dynamic"
                            ? ""
                            : this.media?.asset?.url || null;
                    } else if (headerComponent.format === "TEXT") {
                        headerText = headerComponent.text;
                    }
                }

                return {
                    headerType,
                    headerMediaType: headerType,
                    headerMediaURL,
                    headerText,
                    footer: footerComponent ? footerComponent.text : null,
                    body: bodyComponent ? bodyComponent.text : "",
                    buttons: buttonsComponent ? buttonsComponent.buttons : [],
                };
            }

            // Gupshup format için (whatsapp-business-api)
            const metaObject = typeof template.meta === "string" ? JSON.parse(template.meta) : template.meta || {};
            const containerMetaObj = typeof template.containerMeta === "string" 
                ? JSON.parse(template.containerMeta) 
                : template.containerMeta || {};

            return {
                headerType: template.templateType, //text|image,
                headerMediaType: template.templateType, //image|video|document|location,
                headerMediaURL:
                    this.mediaType === "dynamic"
                        ? ""
                        : this.media?.asset
                        ? this.media?.asset.url
                        : template.asset && (template.asset.v2Format && template.asset.cdnUrl
                          ? template.asset.cdnUrl
                          : `https://static.supsis.live/${template.asset.path}${template.asset.id}.${template.asset.ext}`),
                headerText: _.get(containerMetaObj, "header"),
                footer: _.get(containerMetaObj, "footer"),
                body: _.get(metaObject, "example") || metaObject.data,
                buttons: _.get(containerMetaObj, "buttons") || template.buttons,
            };
        },
        getChannelTemplates() {
            if (!this.clone.channelId) return;
            this.loading = true;
            this.getTemplates(this.clone.channelId).then((templates) => {
                this.loading = false;
                this.templates = templates;
                if (this.clone?.asset?.url) {
                    this.media = this.clone.asset && {
                        ...this.clone?.asset,
                        data: this.clone?.asset?.url,
                        asset: this.clone?.asset,
                    };
                }
                this.selectedTemplate = this.selectTemplate(this.clone.template?.id);
            });
        },
        selectTemplate(templateId) {
            const template = this.templates.find((t) => t.id === templateId);
            this.selectedTemplateId = templateId;
            this.clone.template = template;
            if (template) {
                this.getParams();
            }
        },
        getParams() {
            const template = this.clone.template;
            if (!template) return;

            // Meta format için (whatsapp-business-api-supsis)
            if (template.format === "meta" && template.components) {
                const components = template.components || [];
                const headerComponent = components.find((c) => c.type === "HEADER");
                const bodyComponent = components.find((c) => c.type === "BODY");
                const buttonsComponent = components.find((c) => c.type === "BUTTONS");

                let variables = [];

                // Header variables
                const headerText = headerComponent?.text || "";
                const headerVars = headerText.match(/\{\{(\d+)\}\}/g) || [];
                variables = [...variables, ...headerVars];

                // Body variables
                const bodyText = bodyComponent?.text || "";
                const bodyVars = bodyText.match(/\{\{(\d+)\}\}/g) || [];
                variables = [...variables, ...bodyVars];

                // Button URL paramları (WABA: URL button başına 1 param tüketilir, URL içinde {{}} varsa)
                const buttons = buttonsComponent?.buttons || [];
                buttons.forEach((btn) => {
                    if (btn.type === "URL" && btn.url && btn.url.includes("{{")) {
                        variables.push("{{button_url}}");
                    }
                });

                this.variables = _.uniq(variables).map(() => "");
                this.clone.params = new Array(this.variables.length).fill("").map((val, index) => {
                    return this.clone.params[index] || val;
                });
                return;
            }

            // Gupshup format için (whatsapp-business-api)
            const templateType = template.templateType;
            const containerMetaObj = typeof template.containerMeta === "string" 
                ? JSON.parse(template.containerMeta) 
                : template.containerMeta || {};
            const headerText = _.get(containerMetaObj, "header", "");
            let cardVariableSize = 0;
            if (templateType === "CAROUSEL") {
                const cards = containerMetaObj.cards;
                cardVariableSize = cards
                    .map((card) => {
                        return card.body.match(/{{\d+}}/g);
                    })
                    .flat()
                    .filter(Boolean).length;
            }
            const buttonVariableSize = (containerMetaObj.buttons || []).filter((button) => button.type === "URL").map((button) => {
                return button.url.match(/{{\d+}}/g);
            }).flat().filter(Boolean).length;
            
            const bodyText = template.data || "";
            this.variables = [
                ..._.uniq(headerText.match(/{{\d+}}/g) || []),
                ..._.uniq(bodyText.match(/{{\d+}}/g) || []),
                ...new Array(cardVariableSize).fill(null),
                ...new Array(buttonVariableSize).fill(null),
            ].filter(Boolean).map(() => "");
            this.clone.params = new Array(this.variables.length).fill("").map((val, index) => {
                return this.clone.params[index] || val;
            });
        },
        getVariableValue(index) {
            return this.clone.params[index] || "";
        },
        setVariableValue(index, val) {
            console.log("setVariableValue", {
                index,
                val,
            });
            this.clone.params = this.clone.params.map((value, paramIndex) => {
                if (index === paramIndex) value = val;
                return value;
            });
        },
        async handleFileUpload(event) {
            const file = event.target.files[0];
            if (file) {
                const imageMime = ["image/jpeg", "image/png"];
                const videoMime = ["video/mp4"];
                const pdfMimeType = ["application/pdf"];
                const mb = file.size / 1024 / 1024;
                if (imageMime.includes(file.type) && mb >= 2) {
                    this.$message.error("The image could not be uploaded because it is more than 2 MB.", 3);
                    return;
                }
                if (videoMime.includes(file.type) && mb >= 20) {
                    this.$message.error("The video could not be uploaded because it is more than 20 MB.", 3);
                    return;
                }

                if (pdfMimeType.includes(file.type) && mb >= 2) {
                    this.$message.error("The document could not be uploaded because it is more than 2 MB.", 3);
                    return;
                }

                this.$message.loading("Image uploading, please wait...", 5);

                let path = `${this.siteId}/whatsapp-business/${this.clone.channelId}/assets/`;
                let imgUploadData = await uploadAsset({
                    path,
                    name: file.name,
                    size: file.size,
                    type: "gupshup-file",
                    mime: file.type,
                    ext: file.name.split(".").pop(),
                });

                await axios({
                    method: imgUploadData.data.method,
                    url: imgUploadData.data.url,
                    data: file,
                    headers: { "Content-Type": file.type },
                });

                this.media = {
                    data: URL.createObjectURL(file),
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    asset: {
                        id: imgUploadData.data.asset.id,
                        url: imgUploadData.data.asset.v2Format && imgUploadData.data.asset.cdnUrl
                        ? imgUploadData.data.asset.cdnUrl
                        :`https://static.supsis.live/${imgUploadData.data.asset.path}${imgUploadData.data.asset.id}.${imgUploadData.data.asset.ext}`,
                        name: file.name,
                        ext: imgUploadData.data.asset.ext,
                        path: imgUploadData.data.asset.path,
                    },
                };

                this.clone.asset = this.media.asset;
                this.$message.success("Resim başarılı bir şekilde yüklendi.");
            }
        },
        getAcceptType() {
            const template = this.clone.template;
            if (!template) return "";

            let templateType = null;

            // Meta format için (whatsapp-business-api-supsis)
            if (template.format === "meta" && template.components) {
                const headerComponent = template.components.find((c) => c.type === "HEADER");
                if (headerComponent && headerComponent.format) {
                    templateType = headerComponent.format; // IMAGE, VIDEO, DOCUMENT
                }
            } else {
                // Gupshup format için (whatsapp-business-api)
                templateType = template.templateType;
            }

            let types = [];
            if (templateType === "IMAGE") {
                types = _.concat(types, ["image/jpeg", "image/png"]);
            } else if (templateType === "VIDEO") {
                types = _.concat(types, ["video/mp4"]);
            } else if (templateType === "DOCUMENT") {
                types = _.concat(types, ["application/pdf"]);
            }
            return types.join(" ,");
        },
        deleteMedia() {
            this.media = null;
            this.clone.asset = null;
        },
    },
    mounted() {
        this.mediaType = this.clone.asset?.externalUrl ? "dynamic" : "static";
        if (this.mediaType === "dynamic") this.externalUrl = this.clone.asset?.externalUrl || "";
        this.getChannelTemplates();
    },
    watch: {
        mediaType: {
            deep: true,
            handler() {
                if (this.mediaType === "static" && this.clone.asset?.externalUrl) {
                    this.clone.asset = null;
                }
            },
        },
        externalUrl: {
            deep: true,
            handler() {
                if (this.mediaType === "dynamic") {
                    this.clone.asset = {
                        externalUrl: this.externalUrl,
                    };
                }
            },
        },
        clone: {
            deep: true,
            handler(newValue) {
                this.$emit("input", newValue);
            },
        },
        "clone.channelId": {
            deep: true,
            handler() {
                this.getChannelTemplates();
            },
        },
    },
};
</script>

<style lang="scss">
.template-preview-modal {
    .custom-modal-header {
        display: none !important;
    }

    .ant-modal-content {
        background: transparent !important;
        box-shadow: none;
    }

    .ant-modal-body {
        background: transparent !important;
    }

    .custom-modal-body {
        background: transparent !important;
        display: flex;
        align-items: center;
        justify-content: center;
    }
}

.block-editor.whatsapp-business-message {
    padding: 20px;
    display: flex;
    align-items: flex-start;
    gap: 20px;

    .ant-spin-nested-loading {
        width: 300px;
    }

    .ant-spin-container {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }

    .editor-row {
        width: 100%;

        .editor-row-title {
            font-weight: bold;
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 5px;
        }
    }

    .phone-number-hint {
        margin-top: 6px;
        font-size: 12px;
        color: #666;
        line-height: 1.4;
    }

    .wpa-variables {
        margin-top: 15px;
    }

    .image-picker {
        margin-top: 15px;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 15px;

        .input-warn {
            background: #fff6e6;
            padding: 20px;

            .list-item {
                padding-left: 15px;
            }
        }

        .media-preview {
            display: flex;
            align-items: center;
            justify-content: space-between;

            .media-preview-content {
                display: flex;
                align-items: center;
                gap: 10px;

                .media-preview-name {
                    font-weight: bold;
                }

                .media-image img {
                    height: 100px;
                }
            }

            .media-preview-delete {
                background: red;
                padding: 5px;
                border-radius: 100%;
                color: white;
                line-height: 0;
            }
        }

        .media-drag {
            display: flex;
            align-items: center;
            flex-direction: column;
            gap: 10px;
            border: 1px solid var(--color-border-default);
            padding: 40px;
            cursor: pointer;
            position: relative;

            .input {
                width: 100%;
                height: 100%;

                input {
                    width: 100%;
                    height: 100%;
                    position: absolute;
                    top: 0;
                    left: 0;
                    opacity: 0;
                }
            }

            .material-icons {
                font-size: 28px;
            }

            .content {
                font-size: 18px;
                font-weight: bold;
            }
        }
    }

    .classic-new-select {
        width: 100%;
        font-weight: bold;

        [role="combobox"] {
            padding: 4px;
            border: none !important;
            border-radius: 4px !important;
            background-color: #f1f1f1 !important;
        }
    }
}
</style>
